import {
  Button,
  ButtonList,
  ClassName,
  ITableColumn,
  Label,
  Loader,
  Position,
  Size,
  Table,
  TextAlign,
  Tray,
} from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { EmptyState, Header, MoreButton } from 'components';
import { useDebounce, useOpenable, useQueryParams, useTableSorting } from 'hooks';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import ManageCertificationsActionBar from '../ManageCertificationsActionBar/ManageCertificationsActionBar';
import { useGetCertifications } from '../hooks/useGetCertifications';
import { getRenewalPeriodMonths, getTrueFalseValueFromString, mapCertificationsToTableData } from '../utils';
import { CreateCertificationDialog } from '../CreateCertificationDialog/CreateCertificationDialog';
import { ICertificationFormData } from '../CertificationForm/CertificationForm';
import { useUpdateCertification } from '../hooks/useUpdateCertification';
import { useCreateCertification } from '../hooks/useCreateCertification';
import { CertificationCreateInput, CertificationUpdateInput } from '__generated__/graphql';
import { isoTimeStampUtc } from 'utils/dateUtils';
import SearchBar from 'components/foundation/SearchBar/SearchBar';
import { Nullable } from 'types/util/Nullable';
import { getLowerCaseSearchPredicate } from 'utils/stringUtils';
import './ManageCertificationsTable.scss';
import { EditCertificationDialog } from '../EditCertificationDialog/EditCertificationDialog';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { QueryParam } from 'utils/constants/queryParam';

export interface IManageCertificationsTableProps {
  className?: ClassName;
}

export interface ManageCertificationsTableData {
  id: string;
  name: string;
  documentationRequired: boolean;
  renewalRequired: boolean;
  employeeCanManage: boolean;
  description?: string | null;
  renewalPeriod?: number;
}

const ManageCertificationsTable = (props: IManageCertificationsTableProps) => {
  const { className } = props;

  const { getParam } = useQueryParams();

  const documentationRequiredParam = getParam(QueryParam.DOCUMENTATION_REQUIRED);
  const renewalRequiredParam = getParam(QueryParam.RENEWAL_REQUIRED);
  const employeeCanManageParam = getParam(QueryParam.EMPLOYEE_CAN_MANAGE);

  const createDetails = useOpenable();
  const editDetails = useOpenable();
  const deleteDetails = useOpenable();
  const loaderDetails = useOpenable();

  const [data, setData] = useState<ManageCertificationsTableData[]>([]);
  const [checkedItems, setCheckedItems] = useState<ManageCertificationsTableData[]>([]);
  const [selectedItem, setSelectedItem] = useState<ManageCertificationsTableData>();
  const { sorted, onSort, sortedBy, sortDirection, sortIsDirty } = useTableSorting(data);
  const [taskNameSearch, setTaskNameSearch] = useState<Nullable<string>>(null);
  const searchDebounced = useDebounce(taskNameSearch, 250);

  const filteredRows = useMemo(() => {
    const filteredData = sorted.filter((item) => {
      return (
        (!isNil(documentationRequiredParam)
          ? item.documentationRequired === getTrueFalseValueFromString(documentationRequiredParam)
          : true) &&
        (!isNil(renewalRequiredParam)
          ? item.renewalRequired === getTrueFalseValueFromString(renewalRequiredParam)
          : true) &&
        (!isNil(employeeCanManageParam)
          ? item.employeeCanManage === getTrueFalseValueFromString(employeeCanManageParam)
          : true)
      );
    });
    const searchedData = filteredData.filter(({ name }) =>
      getLowerCaseSearchPredicate(name ?? '', searchDebounced ?? '')
    );
    return searchedData;
  }, [sorted, documentationRequiredParam, renewalRequiredParam, employeeCanManageParam, searchDebounced]);

  const getCertifications = useGetCertifications();
  const createCertification = useCreateCertification();
  const updateCertification = useUpdateCertification();

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getData() {
    loaderDetails.open();
    const result = await getCertifications();
    setData(mapCertificationsToTableData(result));
    loaderDetails.close();
  }

  async function onCreateCertification(data: ICertificationFormData) {
    const input: CertificationCreateInput = {
      name: data.name!,
      description: data.description,
      renewalPeriodMonths: data.renewalRequired ? getRenewalPeriodMonths(data) : null,
      employeeManaged: data.employeeCanManage ?? false,
      documentationRequired: data.documentationRequired ?? false,
      createdOn: isoTimeStampUtc(),
    };

    await createCertification(input);
    await getData();
    createDetails.close();
  }

  async function onEditCertification(data: ICertificationFormData) {
    const input: CertificationUpdateInput = {
      id: selectedItem!.id,
      name: data.name!,
      description: data.description,
      renewalPeriodMonths: data.renewalRequired ? getRenewalPeriodMonths(data) : null,
      employeeManaged: data.employeeCanManage ?? false,
      documentationRequired: data.documentationRequired ?? false,
    };

    await updateCertification(input);
    await getData();
    setSelectedItem(undefined);
    editDetails.close();
  }

  async function onDeleteConfirmationConfirm() {
    if (selectedItem) {
      const input: CertificationUpdateInput = {
        id: selectedItem.id,
        deletedOn: isoTimeStampUtc(),
      };

      await updateCertification(input);
      await getData();
      setSelectedItem(undefined);
      deleteDetails.close();
    } else {
      const deletePromises = checkedItems.map((item) => {
        const input: CertificationUpdateInput = {
          id: item.id,
          deletedOn: isoTimeStampUtc(),
        };

        return updateCertification(input);
      });

      await Promise.all(deletePromises);
      await getData();
      setCheckedItems([]);
      deleteDetails.close();
    }
  }

  function handleRowClick(row: ManageCertificationsTableData) {
    setSelectedItem(row);
    editDetails.open();
  }

  function handleSelectChange(newCheckedRows: ManageCertificationsTableData[]) {
    setCheckedItems(newCheckedRows);
  }

  function handleSelectAll(checked: boolean) {
    if (checked) {
      handleSelectChange(filteredRows);
    } else {
      handleSelectChange([]);
    }
  }

  function renderEmptyState(): ReactNode {
    return (
      !loaderDetails.isOpen && (
        <EmptyState title={t('No Certifications')} subtitle={t('Use the +New button above to get started.')} />
      )
    );
  }

  function renderActionsColumn(row: ManageCertificationsTableData) {
    const renderDropDown = (closeDropdown: () => void) => {
      const handleDelete = (event: React.MouseEvent) => {
        closeDropdown();
        event.stopPropagation();
        event.preventDefault();

        setSelectedItem(row);
        deleteDetails.open();
      };
      return (
        <ButtonList>
          <Button onClick={handleDelete}>{t('Delete')}</Button>
        </ButtonList>
      );
    };
    return <MoreButton className={`no-print`} position={Position.BOTTOM_START} renderContent={renderDropDown} />;
  }

  function renderNameColumn(row: ManageCertificationsTableData) {
    return <Label>{row.name}</Label>;
  }

  function renderDocumentationRequiredColumn(row: ManageCertificationsTableData) {
    return <Label>{row.documentationRequired ? t('Yes') : t('No')}</Label>;
  }

  function renderRenewalRequiredColumn(row: ManageCertificationsTableData) {
    return <Label>{row.renewalRequired ? t('Yes') : t('No')}</Label>;
  }

  function renderEmployeeCanManageColumn(row: ManageCertificationsTableData) {
    return <Label>{row.employeeCanManage ? t('Yes') : t('No')}</Label>;
  }

  function renderDescriptionColumn(row: ManageCertificationsTableData) {
    return <Label>{row.description}</Label>;
  }

  function renderColumns(): Array<ITableColumn<ManageCertificationsTableData>> {
    return [
      {
        cell: renderActionsColumn,
        key: 'actions',
        sort: false,
        align: TextAlign.LEFT,
        size: '100px',
      },
      {
        cell: renderNameColumn,
        title: t('Name'),
        key: 'name',
        sort: true,
        align: TextAlign.LEFT,
      },
      {
        cell: renderDocumentationRequiredColumn,
        title: t('Req. Doc'),
        key: 'documentationRequired',
        sort: true,
        align: TextAlign.LEFT,
        size: '150px',
      },
      {
        cell: renderRenewalRequiredColumn,
        title: t('Renewal'),
        key: 'renewalRequired',
        sort: true,
        align: TextAlign.LEFT,
        size: '150px',
      },
      {
        cell: renderEmployeeCanManageColumn,
        title: t('Emp. Managed'),
        key: 'employeeCanManage',
        sort: true,
        align: TextAlign.LEFT,
        size: '150px',
      },
      {
        cell: renderDescriptionColumn,
        title: t('Description'),
        key: 'description',
        sort: false,
        align: TextAlign.LEFT,
      },
    ];
  }

  function renderActionBar() {
    return (
      <ManageCertificationsActionBar
        checkedIds={checkedItems.map((item) => item.id)}
        onClear={() => setCheckedItems([])}
        onDelete={deleteDetails.open}
      />
    );
  }

  const classes = classNames('manage-certifications-table', className);

  return (
    <div className={classes}>
      <Header actionBar={renderActionBar()} className={'mb-8'}>
        <Tray align="left">
          <Button rounded={true} type="secondary" size={Size.SMALL} onClick={createDetails.open}>
            {t('+New')}
          </Button>
        </Tray>
        <Tray align="right" className="search-tray">
          <SearchBar className="mx-4" search={taskNameSearch ?? ''} onSearch={setTaskNameSearch} />
        </Tray>
      </Header>
      <Table<ManageCertificationsTableData>
        cols={renderColumns()}
        data={filteredRows}
        strokeCols={false}
        lazyLoad={false}
        minWidth={`${renderColumns().length * 200}px`}
        onRowClick={handleRowClick}
        sortDir={sortDirection}
        sortBy={sortedBy}
        onSort={onSort}
        sortIsDirty={sortIsDirty}
        selected={checkedItems}
        header="standard"
        onSelectChange={handleSelectChange}
        emptyTemplate={renderEmptyState()}
        onCheckAllChange={handleSelectAll}
      />
      <Loader isOpen={loaderDetails.isOpen} overlay={true} />
      <CreateCertificationDialog
        isOpen={createDetails.isOpen}
        onClose={createDetails.close}
        onCreate={onCreateCertification}
      />
      {selectedItem && (
        <EditCertificationDialog
          isOpen={editDetails.isOpen}
          onClose={() => {
            setSelectedItem(undefined);
            editDetails.close();
          }}
          onEdit={onEditCertification}
          certification={selectedItem}
          onDelete={deleteDetails.open}
        />
      )}
      <DeleteConfirmationDialog
        isOpen={deleteDetails.isOpen}
        onClose={() => {
          if (editDetails.isOpen === false) {
            setSelectedItem(undefined);
          }
          deleteDetails.close();
        }}
        deleteText={t('Delete')}
        onDelete={onDeleteConfirmationConfirm}
        title={t('Delete Certification?')}
        showHeaderDivider={false}
      >
        {t(
          `This action will permanently delete the certification, as well as any attached uploads. Are you sure you want to proceed?`
        )}
      </DeleteConfirmationDialog>
    </div>
  );
};

export default ManageCertificationsTable;
